<script>
  import { onMount } from 'svelte';

  // State for teachers
  let teachers = [];
  let teacherQuery = '';
  let filteredTeachers = [];
  let selectedTeacher = null;
  let showTeacherDropdown = false;

  // State for students
  let students = [];
  let studentQuery = '';
  let filteredStudents = [];
  let selectedStudent = null;
  let showStudentDropdown = false;

  // State for shared classes
  let sharedClasses = [];
  let loadingClasses = false;
  let classesError = null;

  // State for class selection and form
  let selectedClass = null;
  let responsibility = '';
  let type = '';
  let comment = '';
  let submitting = false;
  let submitError = null;
  let submitSuccess = false;

  // Fetch teachers
  async function fetchTeachers() {
    const url = '/studentplayground/api/get_google_calendar_ids/';
    const token = localStorage.getItem('jwtToken');
    if (!token) return;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      teachers = data.teachers || [];
      filteredTeachers = teachers;
    } catch (error) {
      console.error('Failed to fetch teachers:', error);
    }
  }

  // Fetch students
  async function fetchStudents() {
    const url = '/studentplayground/get_all_students/';
    const token = localStorage.getItem('jwtToken');
    if (!token) return;
    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      students = data.students || [];
      filteredStudents = students;
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  }

  // Fetch shared classes between selected teacher and student
  async function fetchSharedClasses(teacherId, studentId) {
    loadingClasses = true;
    classesError = null;
    const url = '/studentplayground/api/get_shared_classes/';
    const token = localStorage.getItem('jwtToken');
    
    if (!token) {
      classesError = 'JWT token not found. Please log in.';
      loadingClasses = false;
      return;
    }
  
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          teacher_id: teacherId,
          student_id: studentId
        })
      });
  
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
  
      const data = await response.json();
      sharedClasses = data.classes || [];
    } catch (error) {
      classesError = `Failed to fetch shared classes: ${error.message}`;
      sharedClasses = [];
    } finally {
      loadingClasses = false;
    }
  }

  // Filter teachers as user types
  $: filteredTeachers = teacherQuery
    ? teachers.filter(t => t.name.toLowerCase().includes(teacherQuery.toLowerCase()))
    : teachers;
  $: showTeacherDropdown = teacherQuery && filteredTeachers.length > 0 && !selectedTeacher;

  // Filter students as user types
  $: filteredStudents = studentQuery
    ? students.filter(s => `${s.first_name} ${s.last_name}`.toLowerCase().includes(studentQuery.toLowerCase()))
    : students;
  $: showStudentDropdown = studentQuery && filteredStudents.length > 0 && !selectedStudent;

  // Fetch classes when both teacher and student are selected
  $: if (selectedTeacher && selectedStudent) {
    fetchSharedClasses(selectedTeacher.id, selectedStudent.id);
  }

  // On mount, fetch both lists
  onMount(() => { 
    fetchTeachers();
    fetchStudents();
  });

  function selectTeacher(teacher) {
    selectedTeacher = teacher;
    teacherQuery = teacher.name;
    showTeacherDropdown = false;
  }

  function selectStudent(student) {
    selectedStudent = student;
    studentQuery = `${student.first_name} ${student.last_name}`;
    showStudentDropdown = false;
  }

  function selectClass(classItem) {
    selectedClass = classItem;
    // Reset form when selecting a new class
    responsibility = '';
    type = '';
    comment = '';
  }

  function clearSelection() {
    selectedClass = null;
    responsibility = '';
    type = '';
    comment = '';
    submitError = null;
    submitSuccess = false;
  }

  async function submitResponsibility() {
    // Reset previous states
    submitError = null;
    submitSuccess = false;
    submitting = true;

    // Validate form
    if (!responsibility) {
      submitError = 'Please select who is responsible.';
      submitting = false;
      return;
    }
    if (!type) {
      submitError = 'Please select the incident type.';
      submitting = false;
      return;
    }
    if (!selectedClass) {
      submitError = 'No class selected.';
      submitting = false;
      return;
    }

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      submitError = 'JWT token not found. Please log in.';
      submitting = false;
      return;
    }

    try {
      const response = await fetch('/studentplayground/api/submit_class_responsibility/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
                 body: JSON.stringify({
           class_data: {
             id: selectedClass.id,
             name: selectedClass.name || selectedClass.subject,  // Send full name
             subject: selectedClass.subject,  // Keep for backward compatibility
             teacher: selectedClass.teacher,
             student: selectedClass.student,
             date: selectedClass.date,
             start_time: selectedClass.start_time,
             end_time: selectedClass.end_time
           },
          responsibility: responsibility,
          incident_type: type,
          comment: comment.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        submitSuccess = true;
        
        // If it was a cancellation (Atsaukimas), refresh the shared classes list
        if (type === 'Atsaukimas') {
          // Remove the cancelled class from the current list immediately
          sharedClasses = sharedClasses.filter(c => c.id !== selectedClass.id);
          
          // Refresh the full list from server to ensure consistency
          setTimeout(() => {
            if (selectedTeacher && selectedStudent) {
              fetchSharedClasses(selectedTeacher.id, selectedStudent.id);
            }
          }, 1000);
        }
        
        // Clear form after successful submission
        setTimeout(() => {
          clearSelection();
        }, 2000);
      } else {
        submitError = data.error || 'Failed to submit responsibility form.';
      }
    } catch (error) {
      submitError = `Network error: ${error.message}`;
    } finally {
      submitting = false;
    }
  }
</script>

<div>
  <label for="teacher-input"><strong>Select Teacher:</strong></label>
  <input
    id="teacher-input"
    type="text"
    placeholder="Search teacher..."
    bind:value={teacherQuery}
    on:input={() => {
      if (selectedTeacher && teacherQuery !== selectedTeacher.name) {
        selectedTeacher = null;
        showTeacherDropdown = true;
      }
    }}
    autocomplete="off"
  />
  {#if showTeacherDropdown}
    <ul class="autocomplete-list">
      {#each filteredTeachers.slice(0, 8) as teacher}
        <li>
          <button
            type="button"
            on:click={() => selectTeacher(teacher)}
            on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectTeacher(teacher)}
          >
            {teacher.name}
          </button>
        </li>
      {/each}
    </ul>
  {/if}
  {#if selectedTeacher}
    <div class="selected">Selected Teacher: {selectedTeacher.name}</div>
  {/if}
</div>

<div style="margin-top: 2em;">
  <label for="student-input"><strong>Select Student:</strong></label>
  <input
    id="student-input"
    type="text"
    placeholder="Search student..."
    bind:value={studentQuery}
    on:input={() => {
      if (selectedStudent && studentQuery !== `${selectedStudent.first_name} ${selectedStudent.last_name}`) {
        selectedStudent = null;
        showStudentDropdown = true;
      }
    }}
    autocomplete="off"
  />
  {#if showStudentDropdown}
    <ul class="autocomplete-list">
      {#each filteredStudents.slice(0, 8) as student}
        <li>
          <button
            type="button"
            on:click={() => selectStudent(student)}
            on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectStudent(student)}
          >
            {student.first_name} {student.last_name}
          </button>
        </li>
      {/each}
    </ul>
  {/if}
  {#if selectedStudent}
    <div class="selected">Selected Student: {selectedStudent.first_name} {selectedStudent.last_name}</div>
  {/if}
</div>

<!-- Shared Classes Section -->
{#if selectedTeacher && selectedStudent}
  <div style="margin-top: 3em;">
    <h3>Shared Classes</h3>
    <p>Classes between <strong>{selectedTeacher.name}</strong> and <strong>{selectedStudent.first_name} {selectedStudent.last_name}</strong></p>
    
    {#if loadingClasses}
      <div class="loading">Loading shared classes...</div>
    {:else if classesError}
      <div class="error">{classesError}</div>
        {:else if sharedClasses.length > 0}
      <div class="classes-list">
        {#each sharedClasses as classItem}
          <div 
            class="class-item {selectedClass?.id === classItem.id ? 'selected' : ''} {selectedClass && selectedClass.id !== classItem.id ? 'minimized' : ''}"
            on:click={() => selectClass(classItem)}
            role="button"
            tabindex="0"
            on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectClass(classItem)}
          >
            <h4>{classItem.name || classItem.subject || 'Class'}</h4>
            <p><strong>Date:</strong> {classItem.date}</p>
            <p><strong>Time:</strong> {classItem.start_time} - {classItem.end_time}</p>
            <p><strong>Teacher:</strong> {classItem.teacher}</p>
            <p><strong>Student:</strong> {classItem.student}</p>
            {#if classItem.status}
              <p><strong>Status:</strong> {classItem.status}</p>
            {/if}
          </div>
        {/each}
      </div>

      {#if selectedClass}
        <div class="responsibility-form">
          <h3>Class Responsibility Form</h3>
          <div class="selected-class-info">
            <strong>Selected:</strong> {selectedClass.name || selectedClass.subject} on {selectedClass.date} at {selectedClass.start_time} - {selectedClass.end_time}
          </div>
          
          <div class="form-group">
            <label for="responsibility">Kaltininkas:</label>
            <select id="responsibility" bind:value={responsibility}>
              <option value="">Select responsibility...</option>
              <option value="Teacher">Teacher</option>
              <option value="Student">Student</option>
            </select>
          </div>

          <div class="form-group">
            <label for="type">Tipas:</label>
            <select id="type" bind:value={type}>
              <option value="">Select type...</option>
              <option value="Velavimas">Velavimas</option>
              <option value="Atsaukimas">Atsaukimas</option>
              <option value="Kitas">Kitas</option>
            </select>
          </div>

          <div class="form-group">
            <label for="comment">Comment:</label>
            <textarea 
              id="comment" 
              bind:value={comment} 
              placeholder="Add any additional details..."
              rows="3"
            ></textarea>
          </div>

          {#if submitError}
            <div class="submit-error">{submitError}</div>
          {/if}

          {#if submitSuccess}
            <div class="submit-success">Responsibility assignment submitted successfully!</div>
          {/if}

          <div class="form-actions">
            <button 
              type="button" 
              class="btn-submit"
              on:click={submitResponsibility}
              disabled={submitting || !responsibility || !type}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
            <button type="button" class="btn-cancel" on:click={clearSelection}>Cancel</button>
          </div>
        </div>
      {/if}
    {:else}
      <div class="no-classes">No shared classes found between this teacher and student.</div>
    {/if}
  </div>
{/if}

<style>
  .autocomplete-list {
    border: 1px solid #ccc;
    max-height: 180px;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    list-style: none;
    background: white;
    position: absolute;
    z-index: 10;
    width: 300px;
  }
  .autocomplete-list li {
    padding: 0;
  }
  .autocomplete-list button {
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    padding: 8px 12px;
    cursor: pointer;
    display: block;
  }
  .autocomplete-list button:hover {
    background: #f0f0f0;
  }
  .selected {
    margin-top: 0.5em;
    color: #2a7ae2;
  }
  input[type="text"] {
    width: 300px;
    padding: 8px;
    margin-bottom: 0;
    box-sizing: border-box;
  }
 
   /* Shared Classes Styles */
  .loading {
    color: #666;
    font-style: italic;
    padding: 1em;
  }
  
  .error {
    color: #d32f2f;
    background: #ffebee;
    padding: 1em;
    border-radius: 4px;
    border-left: 4px solid #d32f2f;
  }
  
  .classes-list {
    display: grid;
    gap: 1em;
    margin-top: 1em;
  }
  
  .class-item {
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 1em;
    background: #f9f9f9;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .class-item:hover {
    background: #f0f8ff;
    border-color: #2a7ae2;
  }
  
  .class-item.selected {
    background: #e6f3ff;
    border-color: #2a7ae2;
    border-width: 2px;
  }
  
  .class-item.minimized {
    opacity: 0.4;
    transform: scale(0.95);
  }
  
  .class-item h4 {
    margin: 0 0 0.5em 0;
    color: #2a7ae2;
  }
  
  .class-item p {
    margin: 0.25em 0;
    font-size: 0.9em;
  }
  
  .no-classes {
    color: #666;
    font-style: italic;
    padding: 1em;
    text-align: center;
    background: #f5f5f5;
    border-radius: 4px;
  }

  /* Responsibility Form Styles */
  .responsibility-form {
    margin-top: 2em;
    padding: 1.5em;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: #fff;
  }

  .responsibility-form h3 {
    margin: 0 0 1em 0;
    color: #2a7ae2;
  }

  .selected-class-info {
    background: #f0f8ff;
    padding: 0.75em;
    border-radius: 4px;
    margin-bottom: 1em;
    font-size: 0.9em;
  }

  .form-group {
    margin-bottom: 1em;
  }

  .form-group label {
    display: block;
    margin-bottom: 0.5em;
    font-weight: bold;
    color: #333;
  }

  .form-group select,
  .form-group textarea {
    width: 100%;
    padding: 0.5em;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1em;
    box-sizing: border-box;
  }

  .form-group select:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: #2a7ae2;
    box-shadow: 0 0 0 2px rgba(42, 122, 226, 0.1);
  }

  .form-actions {
    display: flex;
    gap: 1em;
    margin-top: 1.5em;
  }

  .btn-submit,
  .btn-cancel {
    padding: 0.75em 1.5em;
    border: none;
    border-radius: 4px;
    font-size: 1em;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .btn-submit {
    background: #2a7ae2;
    color: white;
  }

  .btn-submit:hover {
    background: #1e5bb8;
  }

  .btn-cancel {
    background: #6c757d;
    color: white;
  }

  .btn-cancel:hover {
    background: #545b62;
  }

  .btn-submit:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  .btn-submit:disabled:hover {
    background: #ccc;
  }

  .submit-error {
    color: #d32f2f;
    background: #ffebee;
    padding: 0.75em;
    border-radius: 4px;
    border-left: 4px solid #d32f2f;
    margin-bottom: 1em;
    font-size: 0.9em;
  }

  .submit-success {
    color: #2e7d32;
    background: #e8f5e8;
    padding: 0.75em;
    border-radius: 4px;
    border-left: 4px solid #2e7d32;
    margin-bottom: 1em;
    font-size: 0.9em;
  }
</style>
    